import type { ICreateOrderApplicationCmd } from "@/data/commands/planning/CreateOrderApplicationCmd";
import type { ICreatePayApplicationCmd } from "@/data/commands/planning/CreatePayApplicationCmd";
import type { IDeliveryArea, IPaymentMethod, IPredefinedPaymentMethod } from "@/data/model/Customer";
import type { IOrderApplication, IOrderApplicationDetails, IOrdererRequirements } from "@/data/model/OrderApplication";
import type { IPayApplication, IPayApplicationWithPaymentMethods } from "@/data/model/PayApplication";
import type { ILinkableEntity } from "@/data/model/Shared";
import type { IUseCasePaymentMethod } from "@/data/model/UseCasePaymentMethod";
import type { IUseCasesQry } from "@/data/queries/planning/UseCasesQry";
import { CurrentIdentityService } from "@/data/services/CurrentIdentityService";
import { Format } from "@/data/util/Format";



import { BaseServiceDao, type IEditableDataSource, type IMultiEntityLinkDataSource, type IPaginatedResult, parseCollection, parseMultiLanguageText, parsePaginatedResult } from "./BaseServiceDao";


export class PlanningDao extends BaseServiceDao {
    public static className = "PlanningDao";

    constructor() {
        super("planning");
        this.isLocationDependent = true;
    }

    //pay use case
    public async getPayApplication(id: string): Promise<IPayApplicationWithPaymentMethods> {
        return this.get("pay-application/" + id, undefined, PlanningDao.parsePayApplicationWithPaymentMethods);
    }

    public async getPayApplications(
        qry: IUseCasesQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IPayApplication>> {
        return this.get(
            "pay-application/",
            qry,
            (f) => parsePaginatedResult(f, PlanningDao.parsePayApplication),
            abortSignal
        );
    }

    public async createPayApplication(cmd: ICreatePayApplicationCmd): Promise<IPayApplicationWithPaymentMethods> {
        return this.post("pay-application/", cmd, PlanningDao.parsePayApplicationWithPaymentMethods);
    }

    public async updatePayApplication(
        id: string,
        cmd: ICreatePayApplicationCmd
    ): Promise<IPayApplicationWithPaymentMethods> {
        return this.put("pay-application/" + id, cmd, PlanningDao.parsePayApplicationWithPaymentMethods);
    }

    public async removePayApplication(id: string): Promise<void> {
        return this.delete("pay-application/" + id, {});
    }

    public payUseCaseDataSource: IEditableDataSource<IPayApplication, IPayApplicationWithPaymentMethods> = {
        getData: (filter, abortSignal) => this.getPayApplications(filter, abortSignal),
        getSingle: (a) => this.getPayApplication(a),
        create: (a) => this.createPayApplication(a),
        update: (a, b) => this.updatePayApplication(a, b),
        remove: (a) => this.removePayApplication(a),
    };

    //article
    public async getOrderApplication(id: string): Promise<IOrderApplicationDetails> {
        return this.get("order-application/" + id, undefined, PlanningDao.parseOrderApplicationDetails);
    }

    public async getOrderApplications(
        qry: IUseCasesQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IOrderApplication>> {
        return this.get(
            "order-application/",
            qry,
            (f) => parsePaginatedResult(f, PlanningDao.parseOrderApplication),
            abortSignal
        );
    }

    public async createUseCase(cmd: ICreateOrderApplicationCmd): Promise<IOrderApplicationDetails> {
        return this.post("order-application/", cmd, PlanningDao.parseOrderApplicationDetails);
    }

    public async updateUseCase(id: string, cmd: ICreateOrderApplicationCmd): Promise<IOrderApplicationDetails> {
        return this.put("order-application/" + id, cmd, PlanningDao.parseOrderApplicationDetails);
    }

    public async removeUseCase(id: string): Promise<void> {
        return this.delete("order-application/" + id, {});
    }

    public useCaseDataSource: IEditableDataSource<IOrderApplication, IOrderApplicationDetails> = {
        getData: (filter, abortSignal) => this.getOrderApplications(filter, abortSignal),
        getSingle: (a) => this.getOrderApplication(a),
        create: (a) => this.createUseCase(a),
        update: (a, b) => this.updateUseCase(a, b),
        remove: (a) => this.removeUseCase(a),
    };

    public useCaseSearch: IMultiEntityLinkDataSource = {
        search: (e, t) =>
            this.get("order-application/search", { Exclude: e, Term: t }, (f) =>
                parseCollection(f, PlanningDao.parseLinkableEntity)
            ),
    };

    public static parseLinkableEntity(literal: any): ILinkableEntity {
        return {
            id: literal.id,
            name: Format.translate(literal.name, CurrentIdentityService.getLanguage()),
            appearsIn:
                literal.appearsIn != null
                    ? parseCollection(literal.appearsIn, PlanningDao.parseLinkableEntity)
                    : undefined,
        };
    }

    public static parseOrderApplicationDetails(literal: any): IOrderApplicationDetails {
        return {
            ...PlanningDao.parseOrderApplication(literal),
            paymentMethods: parseCollection(literal.paymentMethods, PlanningDao.parseUseCasePaymentMethod),
            excludedFulfillmentGroupIds: parseCollection(literal.excludedFulfillmentGroupIds, (c) => c),
            unpaidOrderEmailCustomSubject:
                literal.unpaidOrderEmailCustomSubject == null
                    ? undefined
                    : parseMultiLanguageText(literal.unpaidOrderEmailCustomSubject),
            unpaidOrderEmailCustomContent:
                literal.unpaidOrderEmailCustomContent == null
                    ? undefined
                    : parseMultiLanguageText(literal.unpaidOrderEmailCustomContent),
            orderReminderEmailCustomSubject:
                literal.orderReminderEmailCustomSubject == null
                    ? undefined
                    : parseMultiLanguageText(literal.orderReminderEmailCustomSubject),
            orderReminderEmailCustomContent:
                literal.orderReminderEmailCustomContent == null
                    ? undefined
                    : parseMultiLanguageText(literal.orderReminderEmailCustomContent),
            orderPaymentReceiptEmailCustomSubject:
                literal.orderPaymentReceiptEmailCustomSubject == null
                    ? undefined
                    : parseMultiLanguageText(literal.orderPaymentReceiptEmailCustomSubject),
            orderPaymentReceiptEmailCustomContent:
                literal.orderPaymentReceiptEmailCustomContent == null
                    ? undefined
                    : parseMultiLanguageText(literal.orderPaymentReceiptEmailCustomContent),
            orderReminderPushNotificationTitle: parseMultiLanguageText(literal.orderReminderPushNotificationTitle),
            orderReminderPushNotificationMessage: parseMultiLanguageText(literal.orderReminderPushNotificationMessage),
            reservationConfirmationEmailCustomSubject:
                literal.reservationConfirmationEmailCustomSubject == null
                    ? undefined
                    : parseMultiLanguageText(literal.reservationConfirmationEmailCustomSubject),
            reservationConfirmationEmailCustomContent:
                literal.reservationConfirmationEmailCustomContent == null
                    ? undefined
                    : parseMultiLanguageText(literal.reservationConfirmationEmailCustomContent),
            cancellationEmailCustomSubject:
                literal.cancellationEmailCustomSubject == null
                    ? undefined
                    : parseMultiLanguageText(literal.cancellationEmailCustomSubject),
            cancellationEmailCustomContent:
                literal.cancellationEmailCustomContent == null
                    ? undefined
                    : parseMultiLanguageText(literal.cancellationEmailCustomContent),
            pushNotificationPickUpOrderMessage: parseMultiLanguageText(literal.pushNotificationPickUpOrderMessage),
            pushNotificationPickUpOrderTitle: parseMultiLanguageText(literal.pushNotificationPickUpOrderTitle),

        };
    }

    public static parsePayApplicationWithPaymentMethods(literal: any): IPayApplicationWithPaymentMethods {
        return {
            ...PlanningDao.parsePayApplication(literal),
            paymentMethods: parseCollection(literal.paymentMethods, PlanningDao.parseUseCasePaymentMethod),
            billPaymentReceiptEmailCustomSubject:
                literal.billPaymentReceiptEmailCustomSubject == null
                    ? undefined
                    : parseMultiLanguageText(literal.billPaymentReceiptEmailCustomSubject),
            billPaymentReceiptEmailCustomContent:
                literal.billPaymentReceiptEmailCustomContent == null
                    ? undefined
                    : parseMultiLanguageText(literal.billPaymentReceiptEmailCustomContent),
            cancellationEmailCustomSubject:
                literal.cancellationEmailCustomSubject == null
                    ? undefined
                    : parseMultiLanguageText(literal.cancellationEmailCustomSubject),
            cancellationEmailCustomContent:
                literal.cancellationEmailCustomContent == null
                    ? undefined
                    : parseMultiLanguageText(literal.cancellationEmailCustomContent),
        };
    }

    public static parseDeliveryArea(literal: any): IDeliveryArea {
        return {
            postalCodes: literal.postalCodes,
            minimumOrderSize: literal.minimumOrderSize == null ? undefined : parseFloat(literal.minimumOrderSize),
            freeDeliveryFrom: literal.freeDeliveryFrom == null ? undefined : parseFloat(literal.freeDeliveryFrom),
            deliveryFee: literal.deliveryFee == null ? undefined : parseFloat(literal.deliveryFee),
            id: literal.id,
        }
    }

    public static parseOrderApplication(literal: any): IOrderApplication {
        return {
            id: literal.id,
            consumptionSite: parseInt(literal.consumptionSite),
            fulfillmentPositionDisplayMode: parseInt(literal.fulfillmentPositionDisplayMode),
            fulfillmentHandling: parseInt(literal.fulfillmentHandling),
            schedulingMode: parseInt(literal.schedulingMode),
            ordererRequirements: PlanningDao.parseOrdererRequirements(literal.ordererRequirements),
            consumptionSiteDisplayMode: parseInt(literal.consumptionSiteDisplayMode),
            usePayment: literal.usePayment,
            usePreorder: literal.usePreorder,
            locationId: literal.locationId,
            name: parseMultiLanguageText(literal.name),
            orderPlacedText: parseMultiLanguageText(literal.orderPlacedText),
            beforeOrderPlacedText: parseMultiLanguageText(literal.beforeOrderPlacedText),
            minimumTotalUnitsPerOrder:
                literal.minimumTotalUnitsPerOrder == null ? undefined : parseInt(literal.minimumTotalUnitsPerOrder),
            maximumTotalUnitsPerOrder:
                literal.maximumTotalUnitsPerOrder == null ? undefined : parseInt(literal.maximumTotalUnitsPerOrder),
            areItemCommentsAllowed: literal.areItemCommentsAllowed,
            useTip: literal.useTip,
            useDailyOrderIncrement: literal.useDailyOrderIncrement,


            enableNotifications: literal.enableNotifications,
            enablePushNotificationsOnAndroid: literal.enablePushNotificationsOnAndroid,
            enablePushNotificationsOnIos: literal.enablePushNotificationsOnIos,
            defaultTipPercent: literal.defaultTipPercent,
            enableSignUpBeforePay: literal.enableSignUpBeforePay,
            markUnpaidOrdersOnPrintouts: literal.markUnpaidOrdersOnPrintouts,
            maxPreorderPeriod: literal.maxPreorderPeriod == null ? undefined : literal.maxPreorderPeriod,
            orderPlacedTextIfNotificationsEnabled: parseMultiLanguageText(
                literal.orderPlacedTextIfNotificationsEnabled
            ),
            visibleEndUserProperties: literal.visibleEndUserProperties,
            oneFulfillmentPerOrderMode: parseInt(literal.oneFulfillmentPerOrderMode),
            allowCreatingOrderBills: literal.allowCreatingOrderBills,
            sendEmailConfirmationsForUnpaidOrders: literal.sendEmailConfirmationsForUnpaidOrders,
            sendOrderReminderByPushNotification: literal.sendOrderReminderByPushNotification,
            sendOrderReminderByEmail: literal.sendOrderReminderByEmail,
            useVouchersAndDiscounts: literal.useVouchersAndDiscounts,
            promptOperatorsForReceiptAfterPayment: literal.promptOperatorsForReceiptAfterPayment,
            sendOrderReminderBeforeFulfillmentTime:
                literal.sendOrderReminderBeforeFulfillmentTime == null
                    ? undefined
                    : literal.sendOrderReminderBeforeFulfillmentTime,
            orderBillCustomNameSource: parseInt(literal.orderBillCustomNameSource),
            tableReservationsEnabled: literal.tableReservationsEnabled,
            sendCancellationEmail: literal.sendCancellationEmail,
            sendConfirmationEmailOnReservationAcceptance: literal.sendConfirmationEmailOnReservationAcceptance,
            deliveryAreas: parseCollection(literal.deliveryAreas, PlanningDao.parseDeliveryArea),
            isDeliveryActive: literal.isDeliveryActive,
            operatorDisplayMainItemPricesOnOfferOverview: literal.operatorDisplayMainItemPricesOnOfferOverview,
            operatorDisplayItemImages: literal.operatorDisplayItemImages,
            kioskPaymentPrompt:
                literal.kioskPaymentPrompt == null
                    ? undefined
                    : parseMultiLanguageText(literal.kioskPaymentPrompt)
        };
    }

    public static parseOrdererRequirements(literal: any): IOrdererRequirements {
        return {
            isEmailRequirementEnabled: literal.isEmailRequirementEnabled,
            isFirstNameRequirementEnabled: literal.isFirstNameRequirementEnabled,
            isLastNameRequirementEnabled: literal.isLastNameRequirementEnabled,
            isPhoneNumberRequirementEnabled: literal.isPhoneNumberRequirementEnabled,
            isRegisteredRequirementEnabled: literal.isRegisteredRequirementEnabled,
            isFirstNameRequired: literal.isFirstNameRequired,
            isLastNameRequired: literal.isLastNameRequired,
            isPhoneNumberRequired: literal.isPhoneNumberRequired,
            isRegisteredRequired: literal.isRegisteredRequired,
            firstNameRequirement: parseInt(literal.firstNameRequirement),
            lastNameRequirement: parseInt(literal.lastNameRequirement),
            emailRequirement: parseInt(literal.emailRequirement),
            phoneNumberRequirement: parseInt(literal.phoneNumberRequirement),
            registeredRequirement: parseInt(literal.registeredRequirement),
            isEmailRequired: literal.isEmailRequired,
            enabledFieldRequirementsCount: literal.enabledFieldRequirementsCount,
        };
    }
    public static parsePayApplication(literal: any): IPayApplication {
        return {
            id: literal.id,
            locationId: literal.locationId,
            name: parseMultiLanguageText(literal.name),
            useTip: literal.useTip,
            defaultTipPercent: literal.defaultTipPercent,
            useVouchersAndDiscounts: literal.useVouchersAndDiscounts,
            promptOperatorsForReceiptAfterPayment: literal.promptOperatorsForReceiptAfterPayment,

            sendCancellationEmail: literal.sendCancellationEmail
        };
    }

    public static parseUseCasePaymentMethod(literal: any): IUseCasePaymentMethod {
        return {
            id: literal.id ? literal.id : undefined,
            type: literal.type,
            mode: parseInt(literal.mode),
            name: literal.name == null ? undefined : literal.name,
            paymentMethodId: literal.paymentMethodId,
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            isEnabledForGuests: literal.isEnabledForGuests,
            isEnabledForWaiters: literal.isEnabledForWaiters,
            localizedName: parseMultiLanguageText(literal.localizedName),
            paymentMethodExternalId: literal.paymentMethodExternalId ? literal.paymentMethodExternalId : undefined,
            isCustom: literal.isCustom,
            isQuickPaymentEnabled: literal.isQuickPaymentEnabled,
            isQuickPaymentAvailable: literal.isQuickPaymentAvailable,
            isPaymentDevice: literal.isPaymentDevice,
            revenueEffective: literal.revenueEffective,
            permittedUserIds: parseCollection(literal.permittedUserIds, (c) => c),
        };
    }

    public static parsePaymentMethod(literal: any): IPaymentMethod {
        return {
            id: literal.id ? literal.id : undefined,
            type: literal.type,
            name: literal.name == null ? undefined : literal.name,
            localizedName: {
                de: literal?.localizedName?.de,
                en: literal?.localizedName?.en,
                fr: literal?.localizedName?.fr,
                it: literal?.localizedName?.it,
            },
            mode: parseInt(literal.mode),
            provider: literal.provider == null ? undefined : literal.provider,
            imagePath: literal.imagePath ? literal.imagePath : undefined,
            isCardScheme: literal.isCardScheme,
            displayCollectiveCardIcon: literal.displayCollectiveCardIcon,
            isProviderEditable: literal.isProviderEditable,
            useCollectiveCardIcon: literal.useCollectiveCardIcon,
            isEditEnabled: literal.isEditEnabled,
            isPaymentDevice: literal.isPaymentDevice,
            allowTipOnlyOnDevice: literal.allowTipOnlyOnDevice,
            revenueEffective: literal.revenueEffective,
            isTipAllowed: literal.isTipAllowed,
        };
    }

    public static parsePredefinedPaymentMethod(literal: any): IPredefinedPaymentMethod {
        return {
            id: literal.id ? literal.id : undefined,
            type: literal.type,
            localizedName: {
                de: literal?.localizedName?.de,
                en: literal?.localizedName?.en,
                fr: literal?.localizedName?.fr,
                it: literal?.localizedName?.it,
            },
            mode: parseInt(literal.mode),
            imagePath: literal.imagePath ? literal.imagePath : undefined,
            isCardScheme: literal.isCardScheme,
            displayCollectiveCardIcon: literal.displayCollectiveCardIcon,
            isProviderEditable: literal.isProviderEditable,
            useCollectiveCardIcon: literal.useCollectiveCardIcon,
            isEditEnabled: literal.isEditEnabled,
            isPaymentDevice: literal.isPaymentDevice,
        };
    }
}
